import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useModalInstance } from '@context/ModalInstanceContext'
import { BaseModalType, useModal } from '@context/ModalsContext'

import { Caption1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Modal } from '@ui/components/Modal'
import { ArrowCircleLeftRightIcon } from '@ui/icons/ArrowCircleLeftRight.icon'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { DownloadIcon } from '@ui/icons/Download.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TwoFactorAuthRecoveryCodeDto } from '@model/TwoFactorAuthentication'

import { CopyLink } from '@global/copyLink/CopyLink'

import { usePersistentWindow } from '@hooks/usePersistentWindow'

import { Box } from '@shared/components/Box'
import { InformationModal } from '@shared/modals/Information.modal'

const useTwoFactorPersistentWindow = (condition: boolean): void => {
  const persistentWindowCondition = useCallback(() => condition, [condition])

  usePersistentWindow(persistentWindowCondition)
}

interface Props {
  initialCanClose: boolean
}

export const RecoveryCodesModal: BaseModalType<Props> = (props) => {
  const { close, initialCanClose } = props
  const { t } = useTranslation()
  const { setCanClose, canClose } = useModalInstance()
  const apiClient = useApiClient(ClientApiClient)
  const [isRecoveryCodesLoading, setIsRecoveryCodesLoading] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<TwoFactorAuthRecoveryCodeDto[]>([])

  const recoveryCodesWarningModal = useModal(InformationModal, {
    description: t(
      'Profile.Please copy or download your recovery codes before you close this window'
    ),
  })

  const recoveryCodesCol1 = recoveryCodes.slice(0, Math.ceil(recoveryCodes.length / 2))
  const recoveryCodesCol2 = recoveryCodes.slice(Math.ceil(recoveryCodes.length / 2))

  const recoveryCodesCols = [recoveryCodesCol1, recoveryCodesCol2]

  const isCloseDisabled = isRecoveryCodesLoading || !canClose

  const generateRecoveryCodes = useCallback(async () => {
    try {
      setIsRecoveryCodesLoading(true)
      const response = await apiClient.get2FARecoveryCodesGenerate()
      setRecoveryCodes(response.recoveryCodesDto)
    } catch (error: unknown) {
      console.error(error)
    } finally {
      setIsRecoveryCodesLoading(false)
    }
  }, [apiClient])

  const reGenerateRecoveryCodes = useCallback(async () => {
    try {
      setIsRecoveryCodesLoading(true)
      const response = await apiClient.get2FARecoveryCodesRegenerate()
      setRecoveryCodes(response.recoveryCodesDto)
      setIsRecoveryCodesLoading(false)
      setCanClose(false)
    } catch (error: unknown) {
      console.error(error)
    }
  }, [apiClient, setCanClose])

  const getRecoveryCodesAsText = () => {
    return recoveryCodes.map((code) => code.recoveryCode).join(' ')
  }

  const downloadRecoveryCodesTxtFile = () => {
    const recoveryCodesAsText = recoveryCodes.map((code) => code.recoveryCode).join('\n')

    const blob = new Blob([recoveryCodesAsText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'recoveryCodes.txt'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    setCanClose(true)
  }

  useTwoFactorPersistentWindow(isCloseDisabled)

  useEffect(() => {
    generateRecoveryCodes()
  }, [generateRecoveryCodes])

  useEffect(() => {
    setCanClose(initialCanClose)
  }, [initialCanClose, setCanClose])

  return (
    <Modal>
      <Modal.Header>{t('Profile.Save your recovery codes')}</Modal.Header>
      <Modal.Body isLoading={isRecoveryCodesLoading}>
        <Modal.Body.Text
          dangerouslySetInnerHTML={{
            __html: t(
              `Profile.If you lose access to your device or email, you’ll need to use one of the following recovery codes to log into your Client Area`,
              {
                interpolation: { escapeValue: false },
              }
            ),
          }}
        />
        <Box
          fullWidth
          flex
          wrap
          bg='surface-surface-1'
          radius='x16'
          align='start'
          p='x16'
          justify='space-between'
          gap='x8'
        >
          {recoveryCodesCols.map((recoveryCodesCol, index) => (
            <Box flex direction='column' gap='x8'>
              {recoveryCodesCol.map((recoveryCode, codeIndex) => (
                <Caption1 color='text-med-em' isParagraph>
                  {index * recoveryCodesCol1.length + codeIndex + 1}. {recoveryCode.recoveryCode}
                </Caption1>
              ))}
            </Box>
          ))}
        </Box>
        <Box flex wrap align='center' gap='x8' fullWidth mb='x8'>
          <CopyLink
            label={`${t('Copied')}!`}
            value={getRecoveryCodesAsText()}
            onCopy={() => setCanClose(true)}
          >
            <Button.Ghost iconRight={CopyIcon}>{t('Copy')}</Button.Ghost>
          </CopyLink>
          <Button.Ghost iconRight={DownloadIcon} onClick={() => downloadRecoveryCodesTxtFile()}>
            {t('Download')}
          </Button.Ghost>
          <Button.Ghost
            iconLeft={ArrowCircleLeftRightIcon}
            onClick={() => reGenerateRecoveryCodes()}
          >
            {t(`Profile.Get new codes`)}
          </Button.Ghost>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={canClose ? close : () => recoveryCodesWarningModal.open()}>
          {t('Close')}
        </Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}
